import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Grid,
  CircularProgress,
  Chip
} from '@mui/material';
import { IconDownload, IconTrendingUp, IconPhone } from '@tabler/icons-react';
import api from '../../services/api';
import * as XLSX from 'xlsx-js-style';

interface AgentReportsProps {
  startDate: string;
  endDate: string;
}

const AgentReports: React.FC<AgentReportsProps> = ({ startDate, endDate }) => {
  const [agentStats, setAgentStats] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAgentStats();
  }, [startDate, endDate]);

  const loadAgentStats = async () => {
    setLoading(true);
    try {
      const response = await api.get('/reports/all-agents-performance', {
        params: { startDate, endDate }
      });
      
      if (response.data.success) {
        setAgentStats(response.data.data || []);
      }
    } catch (error) {
      console.error('Error loading agent stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (seconds: number) => {
    if (!seconds) return '-';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  const exportToExcel = () => {
    const exportData = agentStats.map((agent, index) => ({
      'Sıra': index + 1,
      'Agent': `${agent.firstName} ${agent.lastName}`,
      'Email': agent.email,
      'Toplam Çağrı': agent.totalCalls,
      'Gelen': agent.incomingCalls,
      'Giden': agent.outgoingCalls,
      'Cevaplanan': agent.answeredCalls,
      'Kaçan': agent.missedCalls,
      'Ort. Çağrı Süresi': formatDuration(agent.averageDuration),
      'Ort. Bekleme': formatDuration(agent.averageWaitTime),
      'En Uzun Çağrı': formatDuration(agent.longestCall),
      'Toplam Konuşma': formatDuration(agent.totalTalkTime),
      'Başarı Oranı': formatPercentage(agent.successRate)
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    
    // Stil uygula
    const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
    
    // Header stilleri
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const address = XLSX.utils.encode_col(C) + "1";
      if (!ws[address]) continue;
      ws[address].s = {
        fill: { fgColor: { rgb: "10B981" } },
        font: { bold: true, color: { rgb: "FFFFFF" } },
        alignment: { horizontal: "center", vertical: "center" }
      };
    }

    // Tüm hücreleri ortala
    for (let R = range.s.r; R <= range.e.r; ++R) {
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const address = XLSX.utils.encode_cell({ r: R, c: C });
        if (!ws[address]) continue;
        if (!ws[address].s) ws[address].s = {};
        ws[address].s.alignment = { horizontal: "center", vertical: "center" };
      }
    }

    // Sütun genişlikleri
    ws['!cols'] = [
      { wch: 6 },  // Sıra
      { wch: 20 }, // Agent
      { wch: 25 }, // Email
      { wch: 12 }, // Toplam Çağrı
      { wch: 8 },  // Gelen
      { wch: 8 },  // Giden
      { wch: 10 }, // Cevaplanan
      { wch: 8 },  // Kaçan
      { wch: 15 }, // Ort. Çağrı Süresi
      { wch: 12 }, // Ort. Bekleme
      { wch: 12 }, // En Uzun Çağrı
      { wch: 15 }, // Toplam Konuşma
      { wch: 12 }  // Başarı Oranı
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Agent Raporu');
    XLSX.writeFile(wb, `Agent_Raporu_${startDate}_${endDate}.xlsx`);
  };

  return (
    <Box>
      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 3 }}>
          <Card sx={{ bgcolor: '#eff6ff', border: '1px solid #dbeafe' }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Toplam Agent
              </Typography>
              <Typography variant="h4" sx={{ color: '#3b82f6', fontWeight: 'bold' }}>
                {agentStats.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Card sx={{ bgcolor: '#f0fdf4', border: '1px solid #dcfce7' }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Toplam Çağrı
              </Typography>
              <Typography variant="h4" sx={{ color: '#10b981', fontWeight: 'bold' }}>
                {agentStats.reduce((sum, a) => sum + (a.totalCalls || 0), 0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Card sx={{ bgcolor: '#fefce8', border: '1px solid #fef3c7' }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Ort. Başarı Oranı
              </Typography>
              <Typography variant="h4" sx={{ color: '#f59e0b', fontWeight: 'bold' }}>
                {agentStats.length > 0 
                  ? formatPercentage(agentStats.reduce((sum, a) => sum + (a.successRate || 0), 0) / agentStats.length)
                  : '0%'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Card sx={{ bgcolor: '#fef2f2', border: '1px solid #fecaca' }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Kaçan Çağrılar
              </Typography>
              <Typography variant="h4" sx={{ color: '#ef4444', fontWeight: 'bold' }}>
                {agentStats.reduce((sum, a) => sum + (a.missedCalls || 0), 0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Agent Stats Table */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Agent Performans Detayları</Typography>
            <Button
              variant="contained"
              startIcon={<IconDownload size={18} />}
              onClick={exportToExcel}
              disabled={agentStats.length === 0}
            >
              Excel İndir
            </Button>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc' }}>Agent</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc', textAlign: 'center' }}>Toplam Çağrı</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc', textAlign: 'center' }}>Gelen</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc', textAlign: 'center' }}>Giden</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc', textAlign: 'center' }}>Cevaplanan</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc', textAlign: 'center' }}>Kaçan</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc', textAlign: 'center' }}>Ort. Çağrı Süresi</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc', textAlign: 'center' }}>Ort. Bekleme</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc', textAlign: 'center' }}>En Uzun Çağrı</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc', textAlign: 'center' }}>Toplam Konuşma</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc', textAlign: 'center' }}>Başarı Oranı</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {agentStats.map((agent) => (
                    <TableRow key={agent._id} hover>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {agent.firstName} {agent.lastName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {agent.email}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Chip 
                          label={agent.totalCalls || 0} 
                          size="small" 
                          color="primary"
                          icon={<IconPhone size={14} />}
                        />
                      </TableCell>
                      <TableCell align="center">{agent.incomingCalls || 0}</TableCell>
                      <TableCell align="center">{agent.outgoingCalls || 0}</TableCell>
                      <TableCell align="center">
                        <Typography sx={{ color: '#10b981', fontWeight: 500 }}>
                          {agent.answeredCalls || 0}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography sx={{ color: '#ef4444', fontWeight: 500 }}>
                          {agent.missedCalls || 0}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">{formatDuration(agent.averageDuration)}</TableCell>
                      <TableCell align="center">{formatDuration(agent.averageWaitTime)}</TableCell>
                      <TableCell align="center">{formatDuration(agent.longestCall)}</TableCell>
                      <TableCell align="center">
                        <Typography sx={{ fontWeight: 500 }}>
                          {formatDuration(agent.totalTalkTime)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={formatPercentage(agent.successRate)}
                          size="small"
                          sx={{
                            bgcolor: agent.successRate >= 80 ? '#10b981' : agent.successRate >= 60 ? '#f59e0b' : '#ef4444',
                            color: 'white',
                            fontWeight: 'bold'
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default AgentReports;
